import React from 'react';
import { Card, Table } from 'react-bootstrap';
import EmploymentAttestationSummary from '@/components/hiring/EmploymentAttestationSummary';
import RequirementAnswersSummary from '@/components/hiring/RequirementAnswersSummary';
import SharedAnswersSummary from './blocks/SharedAnswersSummary';
import { getProgramDefinition } from './programs';

// A stored answer keeps its line breaks, one paragraph per line.
function Text({ value }) {
    return String(value)
        .split('\n')
        .filter((line) => line.trim() !== '')
        .map((line, index, lines) => (
            <p
                className={index === lines.length - 1 ? 'mb-0' : 'mb-2'}
                key={index}
            >
                {line}
            </p>
        ));
}

function Section({ title, children }) {
    return (
        <Card className="border-0 shadow-sm mb-3">
            <Card.Body>
                <h3 className="h6 mb-2">{title}</h3>
                {children}
            </Card.Body>
        </Card>
    );
}

function Field({ label, children }) {
    return (
        <>
            <dt className="col-sm-4 fw-normal text-body-secondary">{label}</dt>
            <dd className="col-sm-8 mb-1">{children}</dd>
        </>
    );
}

/**
 * The student's own answers, read only. It reads the answer fields and
 * nothing a reviewer wrote: no review notes, reviewer or status history.
 * A reference shows a name and a relationship, never the address.
 * `positionLabels` maps a position's id to its course, e.g. "BIO 1080".
 */
function StudentApplicationAnswers({ application, positionLabels }) {
    const {
        academicBackground,
        availability,
        interestStatement,
        priorExperience,
        references,
        employmentAttestation,
        requirementAttestations,
        positions,
    } = application;
    const definition = getProgramDefinition(application.program);
    const Summary = definition?.Summary;
    // The blocks the program's wizard asks, so an answer is shown in the
    // program's own summary and not a second time
    const hasBlock = (name) =>
        definition
            ? definition.steps.some((step) => step.blocks.includes(name))
            : true;
    const slots = availability?.schedule || [];
    const hasAcademic =
        academicBackground?.major ||
        academicBackground?.secondMajor ||
        academicBackground?.minor ||
        academicBackground?.gpa != null;
    const hasAvailability =
        hasBlock('availability') &&
        (availability?.hoursPerWeek != null || slots.length);

    return (
        <>
            {hasAcademic && (
                <Section title="Academic background">
                    <dl className="row mb-2">
                        {academicBackground.major && (
                            <Field label="Major">
                                {academicBackground.major}
                            </Field>
                        )}
                        {academicBackground.secondMajor && (
                            <Field label="Second major">
                                {academicBackground.secondMajor}
                            </Field>
                        )}
                        {academicBackground.minor && (
                            <Field label="Minor">
                                {academicBackground.minor}
                            </Field>
                        )}
                        {academicBackground.gpa != null && (
                            <Field label="GPA">{academicBackground.gpa}</Field>
                        )}
                    </dl>
                </Section>
            )}

            {requirementAttestations?.length > 0 && (
                <Section title="Your answers for each position">
                    <RequirementAnswersSummary
                        own
                        answers={requirementAttestations}
                        labelFor={(entry) =>
                            positionLabels?.[String(entry.position)] ||
                            `the course for position ${
                                (positions || []).indexOf(entry.position) + 1
                            }`
                        }
                    />
                </Section>
            )}

            {hasAvailability && (
                <Section title="Availability">
                    {availability.hoursPerWeek != null && (
                        <dl className="row mb-2">
                            <Field label="Hours a week">
                                {availability.hoursPerWeek}
                            </Field>
                        </dl>
                    )}
                    {slots.length > 0 && (
                        <Table size="sm" bordered responsive className="mb-0">
                            <caption>Weekly schedule</caption>
                            <thead>
                                <tr>
                                    <th scope="col">Day</th>
                                    <th scope="col">Start</th>
                                    <th scope="col">End</th>
                                </tr>
                            </thead>
                            <tbody>
                                {slots.map((slot, index) => (
                                    <tr key={index}>
                                        <td>{slot.day}</td>
                                        <td>{slot.startTime}</td>
                                        <td>{slot.endTime}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Section>
            )}

            {hasBlock('interestStatement') && interestStatement && (
                <Section title="Interest statement">
                    <Text value={interestStatement} />
                </Section>
            )}

            {hasBlock('priorExperience') && priorExperience && (
                <Section title="Prior experience">
                    <Text value={priorExperience} />
                </Section>
            )}

            {references?.length > 0 && (
                <Section title="References">
                    <ul className="list-unstyled mb-0">
                        {references.map((reference, index) => (
                            <li key={index}>
                                {reference.name}
                                {reference.relationship &&
                                    `, ${reference.relationship}`}
                            </li>
                        ))}
                    </ul>
                </Section>
            )}

            <SharedAnswersSummary
                application={{
                    ...application,
                    availability: hasBlock('availability')
                        ? undefined
                        : availability,
                }}
            />

            {Summary && <Summary application={application} />}

            <EmploymentAttestationSummary attestation={employmentAttestation} />
        </>
    );
}

export default StudentApplicationAnswers;
